import { AuditDetails, Entity, SortableEntity } from '../entity';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { Lex, LexoRank } from '../shared/lexorank';
import { EntityStatusType, Party } from './enum';
import { v7 } from 'uuid';

/**
 * A status set entry. Identity is moving here from the sub-status: the parent becomes the status a
 * record holds, and `subStatuses` become the reasons it may be given.
 *
 * Every field below is optional so the thirteen entity types that share this model — work, site, asset,
 * equipment, recurring job, invoice, contact, agreement and the rest — keep reading it unchanged while
 * only Item, ItemPending and Task are reshaped.
 */
export interface EntityStatus extends AuditDetails {
    _id: string;
    name: string;
    sortOrder?: Lex;

    /** Built-in status that may never be deactivated or deleted; code finds it by `systemName`, or by `isDefault` for a starting status that has none. */
    system?: boolean;

    statusType: EntityStatusType;
    entityType: ActivityEntityName;
    subStatuses: EntitySubStatus[];

    /** The name code matches on. Only a `system` status may be resolved by it. */
    systemName?: string;

    /** Bridge to the pre-entityStatus enums (`CodeStatus`, `TaskStatus`) while readers still filter on them. */
    legacyStatus?: any;

    /** Spelt as `Entity.color`, which the sub-status already stores and the migration copies up. */
    color?: string;

    /** A record moved into this status must be given one of its sub-statuses as the reason. */
    reasonRequired?: boolean;

    /** Who may move a record into this status. Empty means the system alone does. */
    selectableBy?: Party[];

    /**
     * Who may change a record's fields while it holds this status. Empty locks the record for everyone.
     *
     * Field editing only. A party listed in the target status's `selectableBy` may still move the record
     * out, which is what lets a locked Completed task be reopened.
     */
    editableBy?: Party[];

    /** The status a new record of this set is created in. Exactly one Active status per set. */
    isDefault?: boolean;

    active: boolean;
}

/**
 * The sets where the parent is the status and its sub-statuses are its reasons.
 *
 * Named, not sniffed: a parent's fields cannot tell a status from a group.
 */
/**
 * A converted set's status, wearing the sub-status shape.
 *
 * TEMPORARY. `EntityStatusService.getSubStatuses` fakes a parent into the child shape so the old pickers, written to read children, keep working on a set whose parent is now the status; delete that fake and this type together once E1-09 (WBS-11663) has the pickers read parents through `getForEntity`, and the ~70 callers have been sorted by which set they serve.
 *
 * Persisted sub-statuses carry none of these four fields, which is why they do not belong on `EntitySubStatus`.
 */
export type FlattenedEntityStatus = EntitySubStatus & Pick<EntityStatus, 'reasonRequired' | 'selectableBy' | 'editableBy' | 'subStatuses'>;

export const PARENT_IDENTITY_STATUS_SETS: ActivityEntityName[] = [ActivityEntityName.Task, ActivityEntityName.Item, ActivityEntityName.ItemPending];

/**
 * Display name for a status set whose entity name is not what users call it.
 *
 * Shared rather than owned by the settings screen: the `Item` statuses configure SOR lines, so a
 * picker that labels a merged set has to read the same name the tenant configured them under —
 * grouping options as "Item" where Settings says "SOR" describes two things that are one.
 */
export const ENTITY_STATUS_SET_NAMES: Partial<Record<ActivityEntityName, string>> = {
    [ActivityEntityName.SitePro]: 'Site',
    [ActivityEntityName.Work]: 'Job v2',
    [ActivityEntityName.Task]: 'Tasks',
    [ActivityEntityName.Item]: 'Items (Authorised)',
    [ActivityEntityName.ItemPending]: 'Items (Pending)',
};

/** The name to show for a status set — its override, or the entity name itself. */
export function resolveEntityStatusSetName(entity: ActivityEntityName): string {

    return ENTITY_STATUS_SET_NAMES[entity] ?? entity;
}

/**
 * A sub-status. Becoming the reason a status was given, e.g. "access refused" under Cannot Complete.
 *
 * `systemName` and `legacyStatus` stay here as transitional copies of the parent's identity so lines
 * stamped before the flip still resolve. They go once BGIS, Homes NSW and Connect read the parent.
 */
export interface EntitySubStatus extends AuditDetails, Entity {
    sortOrder?: Lex;

    parent?: SortableEntity;
    statusType?: EntityStatusType;
    systemName?: string;

    note?: string;

    /** Used to store the corresponding legacy status to facilitate migration to entityStatus */
    legacyStatus?: any

    /** On a sub-status this marks the status's own hidden default reason, not the set's initial status. */
    isDefault?: boolean;


    /**
     * Read-only marker for the sets outside this epic — a Service Plan in it browses as a preview.
     *
     * Not an item capability: what an Item, ItemPending or Task status permits is `editableBy` on the
     * status, and the client lock on a line is the line's own `isReadonly`.
     */
    isReadonly?: boolean;

    /**
     * Legacy status-level capability, still projected onto a line by the three integrations.
     *
     * The core guards read `editableBy` on the status instead; these survive only until E1-11, E1-12
     * and E1-13 move BGIS, Homes NSW and Connect off them. Nothing sets either to a locking value —
     * the seed writes `null`/`false` and Settings has no field for them — so the projection clears a
     * line's own flags rather than setting them. Do not add a reader.
     */
    allowEdit?: boolean;
    allowDelete?: boolean;
}

export function buildEntityStatus(entity: ActivityEntityName, type: EntityStatusType, options?: Partial<EntityStatus>): EntityStatus {
    return {
        _id: v7(),
        sortOrder: LexoRank.first(),
        name: null,
        statusType: type,
        entityType: entity,
        subStatuses: [],
        active: true,
        ...(options || {}),
    }
}

export function buildEntityStatusSubStatus(status: EntityStatus, options?: Partial<EntitySubStatus>): EntitySubStatus {
    return {
        _id: v7(),
        sortOrder: LexoRank.first(),
        isDefault: false,
        isReadonly: false,
        legacyStatus: null,
        active: true,
        name: null,
        // No `systemName: null` default: the index filters on `$exists: true`, which a null key satisfies,
        // so a set's reasons would all collide on (entityType, null).
        ...(options || {}),
        statusType: status.statusType,
    }
}